'use client';

import { useState, useRef } from 'react';
import { cn } from '@/core/utils/cn';
import { Upload, X, FileText } from 'lucide-react';
import { useTranslation } from '@/core/i18n';

interface FileUploadProps {
  label?: string;
  accept?: string;
  multiple?: boolean;
  maxFiles?: number;
  className?: string;
}

export function FileUpload({ label, accept, multiple, maxFiles = 5, className }: FileUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    const arr = Array.from(newFiles).slice(0, maxFiles - files.length);
    setFiles((prev) => [...prev, ...arr]);
  };
  const { t } = useTranslation();

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && <label className="block text-[12px] font-medium text-hms-textSub">{label}</label>}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-hms-border rounded-xl p-6 text-center cursor-pointer hover:border-hms-textDim transition-colors"
      >
        <input id="shared-file-upload-file-1"
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
        />
        <Upload className="w-8 h-8 text-hms-textDim mx-auto mb-2" />
        <p className="text-[13px] text-hms-textSub">{t('shared.fileUpload.dropFilesOrClick')}</p>
        <p className="text-[11px] text-hms-textDim mt-1">
          {accept ? `Accepted: ${accept}` : 'All file types'} &middot; Max {maxFiles} files
        </p>
      </div>
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((file, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-hms-bg border border-hms-border rounded-lg">
              <FileText className="w-4 h-4 text-hms-textDim flex-shrink-0" />
              <span className="text-[12px] text-hms-textSub truncate flex-1">{file.name}</span>
              <span className="text-[10px] text-hms-textDim">{(file.size / 1024).toFixed(0)}KB</span>
              <button id="shared-file-upload-btn-1" onClick={() => removeFile(i)} className="text-hms-textDim hover:text-hms-red">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
